export interface Payment {
  id: string;
  type: 'credit-card' | 'invoice' | 'aidat' | 'market' | 'other';
  amount: number;
  description: string;
  date: string;
  paid: boolean;
  recurring?: boolean;
  reminder?: boolean;
  marketName?: string;
}

export function turkeyDate(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const part = (type: string) => parts.find(p => p.type === type)!.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function paymentSeriesKey(p: Payment): string {
  return JSON.stringify([p.type, p.description, p.amount, p.marketName || '']);
}

export function paymentOccurrenceKey(p: Payment): string {
  return p.recurring ? JSON.stringify([paymentSeriesKey(p), p.date.slice(0, 7)]) : p.id;
}

// Used by both the page and the scheduled reminder service. No database writes.
export function recurringEntries(payments: Payment[], today: string): Payment[] {
  const month = today.slice(0, 7);
  const [year, monthNumber] = month.split('-').map(Number);
  const existing = new Set(payments.filter(p => p.date.slice(0, 7) === month).map(paymentSeriesKey));
  const templates = new Map<string, Payment>();
  for (const p of [...payments].sort((a, b) => a.date.localeCompare(b.date))) {
    if (p.recurring && p.date.slice(0, 7) < month) templates.set(paymentSeriesKey(p), p);
  }
  const entries: Payment[] = [];
  for (const [key, p] of Array.from(templates)) {
    if (existing.has(key)) continue;
    const day = Math.min(Number(p.date.slice(8, 10)), new Date(year, monthNumber, 0).getDate());
    entries.push({
      ...p, id: `recurring:${month}:${key}`,
      date: `${month}-${String(day).padStart(2, '0')}`, paid: false,
    });
  }
  return entries;
}

export function dueReminders(payments: Payment[], today: string): Payment[] {
  const entries = [...payments, ...recurringEntries(payments, today)];
  const result = new Map<string, Payment>();
  for (const p of entries) {
    if (p.date > today || p.paid || !p.reminder) continue;
    const key = paymentOccurrenceKey(p);
    // A paid copy must suppress older duplicate unpaid copies of the same occurrence.
    if (p.recurring && entries.some(other => other.recurring &&
        paymentOccurrenceKey(other) === key && (other.paid || other.reminder === false))) continue;
    result.set(key, p);
  }
  return Array.from(result.values());
}
